import { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography, Chip } from '@mui/material';
import { GoogleMap, OverlayView } from '@react-google-maps/api';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import NearMeIcon from '@mui/icons-material/NearMe';
import StarIcon from '@mui/icons-material/Star';
import { useNavigate } from 'react-router-dom';
import { getLatLong, calcCrow } from '../common/helpers';

const containerStyle = { width: '100%', height: '350px', borderRadius: '8px' };
const DEFAULT_CENTER = { lat: 23.6345, lng: -102.5528 }; // Centro de México

// Cache de geocodificación por dirección (persiste durante la sesión)
const geoCache = new Map();

// getLatLong no resuelve la promesa cuando el geocode falla → la blindamos con timeout
const geocodeSafe = (dir) => {
  if (geoCache.has(dir)) return Promise.resolve(geoCache.get(dir));
  return Promise.race([
    getLatLong(dir).then(c => c || null).catch(() => null),
    new Promise(res => setTimeout(() => res(null), 5000)),
  ]).then(coords => {
    geoCache.set(dir, coords);
    return coords;
  });
};

const mejorRating = (grupo) => Math.max(0, ...grupo.items.map(s => Number(s.rating) || 0));

const HomeMap = ({ servicios = [], max = 12 }) => {
  const navigate = useNavigate();
  const [markers, setMarkers] = useState([]);
  const [userPos, setUserPos] = useState(null);
  const [mode, setMode] = useState('best'); // 'near' = cerca de ti | 'best' = mejor calificados
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);
  const [mapsReady, setMapsReady] = useState(!!(window.google && window.google.maps));
  const mapRef = useRef(null);

  // El script de Google Maps se carga en index.html; esperar a que esté listo
  useEffect(() => {
    if (mapsReady) return;
    const id = setInterval(() => {
      if (window.google && window.google.maps) {
        setMapsReady(true);
        clearInterval(id);
      }
    }, 300);
    return () => clearInterval(id);
  }, [mapsReady]);

  // Pedir geolocalización una sola vez (si el usuario no la permite, userPos queda null)
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      pos => setUserPos({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => setUserPos(null),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 }
    );
  }, []);

  // Clave estable: solo cambia cuando cambian las direcciones únicas
  const dirKey = [...new Set(
    servicios.map(s => (s.ubicacion || '').trim()).filter(Boolean)
  )].sort().join('|');

  useEffect(() => {
    let cancelled = false;

    const porDireccion = {};
    servicios.forEach(s => {
      const dir = (s.ubicacion || '').trim();
      if (!dir) return;
      if (!porDireccion[dir]) porDireccion[dir] = [];
      porDireccion[dir].push(s);
    });

    const direcciones = Object.keys(porDireccion).slice(0, 40);
    if (direcciones.length === 0) {
      setMarkers([]);
      return;
    }

    setLoading(true);
    Promise.all(direcciones.map(async (dir) => {
      const coords = await geocodeSafe(dir);
      return coords ? { coords, direccion: dir, items: porDireccion[dir] } : null;
    })).then(res => {
      if (cancelled) return;
      let grupos = res.filter(Boolean);

      if (userPos) {
        // Más cercanos al cliente primero
        grupos = grupos
          .map(g => ({ ...g, dist: calcCrow(userPos.lat, userPos.lng, g.coords.lat, g.coords.lng) }))
          .sort((a, b) => a.dist - b.dist);
        setMode('near');
      } else {
        // Sin geolocalización: mejor calificados (el backend ya viene ordenado por rating)
        grupos = grupos.sort((a, b) => mejorRating(b) - mejorRating(a));
        setMode('best');
      }

      setMarkers(grupos.slice(0, max));
      setActive(null);
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [dirKey, userPos, max]);

  // Ajustar el encuadre del mapa a los marcadores (y a la ubicación del usuario si existe)
  useEffect(() => {
    if (!mapRef.current || !window.google || (markers.length === 0 && !userPos)) return;
    try {
      const bounds = new window.google.maps.LatLngBounds();
      markers.forEach(m => bounds.extend(m.coords));
      if (userPos) bounds.extend(userPos);
      mapRef.current.fitBounds(bounds);
      if (markers.length <= 1 && !userPos) mapRef.current.setZoom(13);
    } catch (e) { /* noop */ }
  }, [markers, userPos, mapsReady]);

  const fmtPrecio = (v) => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? `$${n.toFixed(0)}` : '';
  };

  return (
    <Box sx={{ position: 'relative', borderRadius: '8px', overflow: 'hidden', mb: 4, border: '1px solid #F1F5F9' }}>
      {mapsReady ? (
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={userPos || DEFAULT_CENTER}
          zoom={userPos ? 12 : 5}
          onLoad={(map) => { mapRef.current = map; }}
          onUnmount={() => { mapRef.current = null; }}
          options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
        >
          {/* Marcador de la ubicación del usuario */}
          {userPos && (
            <OverlayView position={userPos} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
              <div style={{ transform: 'translate(-50%, -50%)' }}>
                <MyLocationIcon style={{ color: '#7495F8', fontSize: 26, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.4))' }} />
              </div>
            </OverlayView>
          )}

          {markers.map((m, i) => (
            <OverlayView key={i} position={m.coords} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
              <div
                style={{ transform: 'translate(-50%, -100%)', cursor: 'pointer', position: 'relative' }}
                onClick={() => setActive(active === i ? null : i)}
              >
                <LocationOnIcon style={{ color: '#0E2465', fontSize: 38, filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.35))' }} />
                {active === i && (
                  <div style={{
                    position: 'absolute', bottom: 42, left: '50%', transform: 'translateX(-50%)',
                    background: 'white', borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
                    padding: '10px 12px', width: 220, zIndex: 1000
                  }}>
                    {m.items.slice(0, 4).map(s => (
                      <Typography
                        key={s._id}
                        variant="caption"
                        onClick={(e) => { e.stopPropagation(); navigate(`/servicio/${s.userId}/${s._id}`); }}
                        sx={{
                          display: 'block', fontWeight: 700, color: '#0E2465', cursor: 'pointer',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                      >
                        {s.nombre}{fmtPrecio(s.costoServicio) && ` — ${fmtPrecio(s.costoServicio)}`}
                      </Typography>
                    ))}
                    {m.items.length > 4 && (
                      <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                        +{m.items.length - 4} servicio(s) más
                      </Typography>
                    )}
                    <Typography variant="caption" sx={{ display: 'block', color: '#737791', mt: 0.5 }}>
                      {m.direccion}
                      {typeof m.dist === 'number' && ` · a ${m.dist < 1 ? `${Math.round(m.dist * 1000)} m` : `${m.dist.toFixed(1)} km`}`}
                    </Typography>
                  </div>
                )}
              </div>
            </OverlayView>
          ))}
        </GoogleMap>
      ) : (
        <Box sx={{ height: 350, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#F8FAFC' }}>
          <CircularProgress size={24} sx={{ color: '#7495f8' }} />
        </Box>
      )}

      {/* Indicador del modo activo */}
      {mapsReady && markers.length > 0 && (
        <Chip
          icon={mode === 'near'
            ? <NearMeIcon style={{ color: 'white', fontSize: 16 }} />
            : <StarIcon style={{ color: 'white', fontSize: 16 }} />}
          label={mode === 'near' ? 'Cerca de ti' : 'Mejor calificados'}
          size="small"
          sx={{
            position: 'absolute', top: 10, left: 10, bgcolor: '#0E2465', color: 'white',
            fontWeight: 700, boxShadow: 1, '& .MuiChip-icon': { color: 'white' }
          }}
        />
      )}

      {loading && (
        <Box sx={{ position: 'absolute', top: 10, right: 10, bgcolor: 'white', borderRadius: '50%', p: 0.6, boxShadow: 1 }}>
          <CircularProgress size={18} sx={{ color: '#7495f8' }} />
        </Box>
      )}

      {mapsReady && !loading && markers.length === 0 && (
        <Box sx={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: 'rgba(255,255,255,0.75)', pointerEvents: 'none', textAlign: 'center', px: 2
        }}>
          <Typography variant="body2" color="#737791">
            Aún no hay servicios con dirección para mostrar en el mapa.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default HomeMap;
